











































































































































































































































































































































import Vue from 'vue';
import { Type, Func, ObjectType, Types, EntityPrimaryType, EntityIndex, objectEach, Expression, objectToArray, EntityRelation, Entity, DefinitionsEntityReference } from 'expangine-runtime';
import { LiveRuntime } from 'expangine-runtime-live';
import { Registry } from '../runtime/Registry';
import { TypeSettings, TypeUpdateEvent } from '../runtime/types/TypeVisuals';
import { getConfirmation } from './Confirm';
import { renameEntity, addEntity, removeEntity } from './EntityBuilders';
import { getEditEntityIndex } from './EditEntityIndex';
import { getEditEntityTranscoder } from './EditEntityTranscoder';
import { Preferences, PreferenceCategory } from './Preference';
import { getEditRelation, getRelationKindText } from './EditRelation';
import { sendNotification } from './Notify';
import { getEditMethod } from './EditMethod';



const PREF_REMOVE_INDEX = Preferences.define({
  key: 'aliased_remove_index',
  label: 'Remove user-defined type indexes without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_REMOVE_TRANSCODER = Preferences.define({
  key: 'aliased_remove_transcoder',
  label: 'Remove user-defined type transcoders without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_REMOVE_RELATION = Preferences.define({
  key: 'aliased_remove_relation',
  label: 'Remove user-defined type relations without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_REMOVE_METHOD = Preferences.define({
  key: 'aliased_remove_method',
  label: 'Remove user-defined type methods without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});

const PREF_REMOVE_TYPE = Preferences.define({
  key: 'aliased_remove_type',
  label: 'Remove user-defined type without confirmation',
  category: [PreferenceCategory.CONFIRM],
  defaultValue: false,
});


export default Vue.extend({
  props: {
    entity: {
      type: Object as () => Entity,
      required: true,
    },
    registry: {
      type: Object as () => Registry,
      required: true,
    },
    hideName: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    relations: [] as EntityRelation[],
    tab: 0,
  }),
  computed: {
    type(): Type {
      return this.entity.type;
    },
    settings(): TypeSettings {
      if (this.registry.getTypeSettingsValidFor(this.type, this.entity.meta)) {
        return this.entity.meta;
      }
      
      return this.entity.meta = this.registry.getTypeSettings(this.type);
    },
    definesKey(): boolean {
      return !!this.entity && this.entity.primaryType === EntityPrimaryType.GIVEN;
    },
    keyContext(): Type {
      return this.entity.getKeyContext();
    },
    keyReturnType(): Type {
      return Types.many(Types.text(), Types.number());
    },
    keyOutputType(): Type | null {
      return this.entity.key.getType(this.registry.defs, this.keyContext);
    },
    definesDescribe(): boolean {
      return true;
    },
    describeContext(): Type {
      return this.entity.getDescribeContext();
    },
    describeReturnType(): Type {
      return Types.text();
    },
    describeOutputType(): Type | null {
      return this.entity.describe.getType(this.registry.defs, this.describeContext);
    },
    indices(): EntityIndex[] {
      const indices: EntityIndex[] = [];
      if (!this.entity) {
        return indices;
      }
      const definedPrimary = this.entity.getPrimary('primary', false);
      if (!definedPrimary) {
        const dynamicPrimary = this.entity.getPrimary();
        if (dynamicPrimary) {
          indices.push(dynamicPrimary);
        }
      }
      objectEach(this.entity.indexes, (index) => indices.push(index));
      return indices;
    },
    transcoders() {
      return objectToArray(this.entity.type.options.props, (_, prop) => ({
        prop,
        transcoder: this.entity ? this.entity.transcoders[prop] || null : null,
      }));
    },
    primaryTypes() {
      return [
        { value: EntityPrimaryType.AUTO_INCREMENT, text: 'Auto Incrementing Number' },
        { value: EntityPrimaryType.UUID, text: 'Universally Unique Identifier' },
        { value: EntityPrimaryType.GIVEN, text: 'User Defined Identifier' },
      ];
    },
    disableStorage(): boolean {
      return !this.entity.name || !!this.validateName(this.entity.name);
    },
    disableData(): boolean {
      return !this.entity.canStore(this.registry.defs);
    },
    zeroReferences(): boolean {
      if (this.entity.instances.length > 0) {
        return false;
      }
      if (this.registry.defs.getEntityReferences(this.entity.name).length > 0) {
        return false;
      }
      return true;
    },
    dataType(): Type {
      return Types.list(this.entity.type);
    },
    dataSettings(): TypeSettings<any> {
      const dataSettings = this.registry.getTypeSettings(this.dataType) as TypeSettings<any, 'item'>;
      dataSettings.sub.item = this.entity.meta;
      return dataSettings;
    },
    references(): DefinitionsEntityReference[] {
      return this.registry.defs.getEntityReferences(this.entity);
    },
  },
  watch: {
    entity: {
      immediate: true,
      handler() {
        this.relations = this.registry.defs.getRelations(this.entity.name);
      },
    },
  },
  methods: {
    renamed(newName: string) {
      const { registry, entity } = this;
      const { defs } = registry;

      if (entity.name) {
        const updates = defs.renameEntity(entity.name, newName);

        if (updates && updates.length) {
          sendNotification({ message: `${updates.length} Type reference(s) updated.` });
        }

        renameEntity(registry, entity.name, newName);
      } else {
        entity.name = newName;

        defs.addEntity(entity);

        addEntity(registry, newName);
      }
    },
    validateName(name: string) {
      if (!name) {
        return 'A name is required.';
      }

      const existing = this.registry.defs.getEntity(name);
      if (existing && existing !== this.entity) {
        return 'An entity already exists with that name.';
      }

      return '';
    },
    async remove() {
      const refs = this.registry.defs.getEntityReferences(this.entity.name).length;

      /**
       * TODO: If has references, do you want to cancel OR replace references with defined type OR remove references?
       */

      if (!await getConfirmation({ message: `Are you sure you want to remove this type? It is referenced ${refs} times.`, pref: PREF_REMOVE_TYPE })) {
        return;
      }

      if (!this.registry.defs.removeEntity(this.entity)) {
        sendNotification({ message: 'You cannot remove a referenced Type.' });
      } else {
        removeEntity(this.registry, this.entity.name);

        this.$emit('remove', this.entity);
      }
    },
    relationsChanged() {
      this.relations = this.registry.defs.getRelations(this.entity.name);
    },
    canEditIndex(name: string) {
      if (!this.entity) {
        return false;
      }
      const index = this.entity.indexes[name];
      if (!index) {
        return false;
      }
      if (index.primary && this.entity.primaryType !== EntityPrimaryType.GIVEN) {
        return false;
      }
      return true;
    },
    async editIndex(index: EntityIndex) {
      const { entity, registry } = this;

      await getEditEntityIndex({
        index,
        entity,
        registry,
      });
    },
    async addIndex() {
      const { entity, registry } = this;

      await getEditEntityIndex({
        entity,
        registry,
      });
    },
    async removeIndex(index: EntityIndex) {
      const { entity } = this;

      if (await getConfirmation({ pref: PREF_REMOVE_INDEX })) {      
        entity.removeIndex(index.name);
      }
    },
    async editTranscoder(property: string) {
      const { entity, registry } = this;

      await getEditEntityTranscoder({
        property,
        entity,
        registry,
      });
    },
    async removeTranscoder(property: string) {
      const { entity } = this;

      if (await getConfirmation({ pref: PREF_REMOVE_TRANSCODER })) {
        entity.removeTranscoder(property);
      }
    },
    getRelationType(typeRelation: EntityRelation) {
      return getRelationKindText(typeRelation.kind);
    },
    async addRelation() {
      const { registry } = this;

      const result = await getEditRelation({
        registry,
      });

      if (result) {
        this.relationsChanged();
      }
    },
    async editRelation(typeRelation: EntityRelation) {
      const { registry } = this;
      
      const result = await getEditRelation({
        registry,  
        relation: typeRelation.relation,
      });

      if (result) {
        this.relationsChanged();
      }
    },
    async removeRelation(typeRelation: EntityRelation) {
      const relations = this.registry.defs.relations;

      if (relations.has(typeRelation.relation.name) && await getConfirmation({ pref: PREF_REMOVE_RELATION })) {
        this.relations.splice(this.relations.indexOf(typeRelation), 1);

        relations.remove(typeRelation.relation.name);
      }
    },
    async addMethod() {
      const { entity, registry } = this;
      
      await getEditMethod({
        entity,
        registry,
      });
    },
    async editMethod(method: Func) {
      const { entity, registry } = this;

      const edited = await getEditMethod({
        entity,
        method,
        registry,
      });

      if (edited) {
        registry.defs.trigger('updateMethod', registry.defs, method, entity);
      }
    },
    async removeMethod(method: Func) {
      if (await getConfirmation({ pref: PREF_REMOVE_METHOD })) {
        this.registry.defs.removeMethod(this.entity, method);
      }
    },
    getTypeDescription(type: Type) {
      return this.registry.getTypeDescribe(type);
    },
    getExpressionDescription(ex: Expression) {
      return this.registry.getExpressionDescribe(ex);
    },
    handleChange(event: TypeUpdateEvent) {
      const { type, settings, transform } = event;

      if (transform) {
        this.registry.defs.refactorEntity(this.entity.name, transform, LiveRuntime);
      }

      this.entity.type = type as ObjectType;
      this.entity.meta = settings;
    },
    onUpdateKey(key: Expression) {
      this.entity.key = key;
      this.entity.updateKeyType(this.registry.defs);
    },
    onPropertyRemove(prop: string) {
      this.registry.defs.removeEntityProp(this.entity.name, prop);
    },
    onPropertyRename([oldProp, newProp]: [string, string]) {
      this.registry.defs.renameEntityProp(this.entity.name, oldProp, newProp);
    },
  },
});
